import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import {Location} from '@angular/common'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { PageHelperService } from '../helpers/page-helper.service';
import { BlackboxPositionDto, ContainerPositionDto, JourneyItemDto, LocationContainersModel, LocationService, MovementService } from 'src/shared/service-proxies/service-proxies';
import { PagebaseComponent } from '../pagebase/pagebase.component';
import { ApplicationStatusService } from '../services/application-status.service';
import * as L from 'leaflet';
import { LocationClassNameHelper } from '../enums/LocationTypeNames';
import { GeoHelper } from '../helpers/geo';
import { GoogleSnappedRoadItem, GoogleSnappedRoadResult, RoadsService } from '../services/roads.service';
import { DateTimeHelper } from '../helpers/datetime';
import { CustomerSettings } from '../services/customer-settings-service';

export interface JobItemModel extends JourneyItemDto {
  journeyFormatted?: string;
  arrivedAtFormatted?: string;
  departedAtFormatted?: string;
  mileageFormatted?: string;
  mileageVarianceFormatted?: string;
}

@Component({
    selector: 'app-container-map',
    templateUrl: './container-map.component.html',
    styleUrls: ['./container-map.component.scss'],
    standalone: false
})
export class ContainerMapComponent extends PagebaseComponent implements OnInit {
  @ViewChild('mapdiv') 
  mapDiv: ElementRef;
  locationDataLoaded: boolean = false;
  data: LocationContainersModel;
  
  // Private variables
  private locationId: string;
  private firstInit: boolean = true;
  
  // Map
  private map;
  private mapLayer = L.tileLayer('http://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}&key=AIzaSyChxd9LP-pHKK1qe1nPlECcK5iJb-a4hg8', {
      maxZoom: 18,
      minZoom: 3,
      subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
  });
  private drawLayer = L.layerGroup();

  constructor(
    private browserLocation: Location,
    pageHelperService: PageHelperService,
    private locationService: LocationService,
    applicationStatusService: ApplicationStatusService,
    private roadsService: RoadsService,
    private route: ActivatedRoute,
    private customerSettings: CustomerSettings) { 
      super(pageHelperService, applicationStatusService, customerSettings, "Container Map");
    }

  ngOnInit() {
    this.route.params.subscribe(({ id }) => {
      if (!this.firstInit) {
        this.locationDataLoaded = false;
        this.getData(false);
      }
    });
    this.firstInit = false;
  }  
  
  ngOnDestroy(): void {
    if (this.map ){
      this.map.eachLayer(function(layer){
          layer.remove();
      });
      this.map.remove();
      this.map = null;
      this.mapDiv.nativeElement.innerHTML = "";
    }
  }

  getData(companyChange: boolean) {
    this.locationId = this.route.snapshot.paramMap.get('id');
    this.locationService.getLocationContainers(this.pageHelperService.selectedCompanyId, this.locationId).subscribe(data => 
    {
      this.data = data;
      this.initMap();
    });
  }

  private initMap(): void {
    if (!this.data?.location || !this.data.containerPositions) {
      return;
    }
    
    let containers = this.data.containerPositions.filter(p => p.position);

    if (!this.map) {
      let zoom = 20;;

      if (containers.length > 0) {
        zoom = -1 + GeoHelper.CalculateZoom(containers.map(p => ({ lat: p.position.latitude, lon: p.position.longitude })), this.mapDiv.nativeElement.offsetHeight, this.mapDiv.nativeElement.offsetWidth);
      }

      this.map = L.map('map', {
        center: [ this.data.location.latitude, this.data.location.longitude ],
        zoom: zoom,
        scrollWheelZoom: false
      });

      this.mapLayer.addTo(this.map);
      this.drawLayer.addTo(this.map);
      setTimeout(() => {
        this.map.invalidateSize(true);
      }, 1)
    } else {
      this.map.flyTo(new L.LatLng(this.data.location.latitude, this.data.location.longitude), 15, {
        animate: true,
        duration: 1.5
      });
    }

    // Clear and re add draw layer
    this.map.removeLayer(this.drawLayer);
    this.drawLayer = L.layerGroup();
    this.drawLayer.addTo(this.map);

    this.addLocationPin(this.data.location.locationType, this.data.location.latitude, this.data.location.longitude, containers.filter(c => !c.isKnown));
    this.addContainerPins(containers.filter(c => c.isKnown));
  }

  addLocationPin(locationTypeName, locationLatitude, locationLongitude, containers: ContainerPositionDto[]) {
    let className = LocationClassNameHelper.getLocationClassName(locationTypeName);
    let title = containers.map(c => this.getContainerLine(c)).join("\n");
    let icon = L.divIcon({
      className: 'custom-div-icon',
      html: "<div class='marker-pin " + className + "'></div><i class='material-icons' title='" + title + "'>home</i>",
      iconSize: [30, 42],
      iconAnchor: [15, 36]
    });
    
    L.marker([locationLatitude, locationLongitude], { icon: icon }).addTo(this.drawLayer);
  }

  addContainerPins(containers: ContainerPositionDto[]) {
    let className = "depot";
    let markers = [];

    this.data.pinsTotal = 0;

    containers.forEach(c => {     
      let marker = markers.find(m => m.latitude == c.position.latitude && m.longitude == c.position.longitude);

      if (marker != null) {
        marker.title += `\n\n${this.getContainerTitle(c)}`;
      }
      else {
        markers.push({
          latitude: c.position.latitude,
          longitude: c.position.longitude,
          title: this.getContainerTitle(c)
        });

        this.data.pinsTotal++;
      };
    });

    markers.forEach(m => {
      let icon = L.divIcon({
        className: 'custom-div-icon',
        html: "<div class='marker-pin " + className + "'></div><i class='material-icons' title='" + m.title + "'>crop_square</i>",
        iconSize: [30, 42],
        iconAnchor: [15, 36]
      });

      L.marker([m.latitude, m.longitude], { icon: icon }).addTo(this.drawLayer);
    });
  }

  getContainerLine(container: ContainerPositionDto): any {
    return `ID: ${container.containerName}, Type: ${container.containerType}, On Site: ${container.daysOnSite} day(s)${(container.externalReference ? `, Contract: ${container.externalReference}`: '')}${(container.wasteTypeName ? `, Waste: ${container.wasteTypeName}`: '')}`;
  }

  getContainerTitle(container: ContainerPositionDto): any {
    return `ID: ${container.containerName}\nType: ${container.containerType}\nOn Site: ${container.daysOnSite} day(s)${(container.externalReference ? `\nContract: ${container.externalReference}`: '')}${(container.wasteTypeName ? `\nWaste: ${container.wasteTypeName}`: '')}`;
  }

  reset() {

  }

  back(): void {
    this.ngOnDestroy();
    this.browserLocation.back()
  }

  paginate (arr, size) {
    return arr.reduce((acc, val, i) => {
      let idx = Math.floor(i / size)
      let page = acc[idx] || (acc[idx] = [])
      page.push(val)
  
      return acc
    }, [])
  }
}
